import AudioPreviewState from '../store/schema/AudioPreviewState';
import IframePreviewState from '../store/schema/IFramePreviewState';
import ImagePreviewState from '../store/schema/ImagePreviewState';
import NativePdfPreviewState from '../store/schema/NativePdfPreviewState';
import PdfJsPreviewState, { PdfJsPasswordState } from '../store/schema/PdfJsPreviewState';
import TextPreviewState from '../store/schema/TextPreviewState';
import VideoPreviewState from '../store/schema/VideoPreviewState';
import WacPreviewState from '../store/schema/WacPreviewState';
import { mutator } from 'satcheljs';
import {
    PreviewPaneBlankViewState,
    PreviewPaneCustomViewState,
    PreviewPaneErrorViewState,
    PreviewPaneLoadingViewState,
    PreviewPaneMode,
    PreviewPaneViewStateBase,
} from '../store/schema/PreviewPaneViewState';
import {
    resetPreviewPane,
    setAudioPreviewState,
    setBlankPreviewState,
    setCustomPreviewState,
    setIframePreviewState,
    setImagePreviewState,
    setImagePreviewStateFromImageGalleryItem,
    setNativePdfPreviewState,
    setPdfJsPasswordState,
    setPdfJsPreviewState,
    setPdfJsPreviewStateByData,
    setPreviewError,
    setPreviewLoading,
    setSaveStatusText,
    setTextPreviewState,
    setVideoPreviewState,
    setWacPreviewState,
    setWacReadOnly,
} from '../actions/setPreviewPaneStateActions';

mutator(resetPreviewPane, actionMessage => {
    const { viewState } = actionMessage;
    viewState.previewPane = null;
});

mutator(setAudioPreviewState, actionMessage => {
    const { viewState, previewUrl, optionalParameters } = actionMessage;
    const audioPreviewState: AudioPreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.Audio,
        previewUrl: previewUrl,
    };

    viewState.previewPane = audioPreviewState;
});

mutator(setBlankPreviewState, actionMessage => {
    const { viewState, optionalParameters } = actionMessage;
    const previewState: PreviewPaneBlankViewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.Blank,
    };

    viewState.previewPane = previewState;
});

mutator(setCustomPreviewState, actionMessage => {
    const { viewState, optionalParameters } = actionMessage;
    const previewState: PreviewPaneCustomViewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.Custom,
    };

    viewState.previewPane = previewState;
});

mutator(setIframePreviewState, actionMessage => {
    const { viewState, previewUrl, optionalParameters } = actionMessage;
    const iFramePreviewState: IframePreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.IFrame,
        previewUrl: previewUrl,
    };

    viewState.previewPane = iFramePreviewState;
});

mutator(setImagePreviewState, actionMessage => {
    const {
        viewState,
        previewUrl,
        imageGalleryItems,
        exportAllowed,
        printDisabled,
        optionalParameters,
    } = actionMessage;
    const imagePreviewState: ImagePreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.Image,
        previewUrl: previewUrl,
        imageLoaded: false,
        isImageLoading: true,
        imageGallery: imageGalleryItems,
        exportAllowed: exportAllowed,
        printDisabled: printDisabled,
    };

    if (
        viewState.previewPane &&
        viewState.previewPane.mode === PreviewPaneMode.Image &&
        viewState.previewPane.previewUrl === previewUrl
    ) {
        imagePreviewState.imageLoaded = viewState.previewPane.imageLoaded;
        imagePreviewState.isImageLoading = viewState.previewPane.isImageLoading;
    }

    viewState.previewPane = imagePreviewState;
});

mutator(setImagePreviewStateFromImageGalleryItem, actionMessage => {
    const { imagePreviewState, imageGalleryItem } = actionMessage;

    if (imagePreviewState.previewUrl !== imageGalleryItem.previewUrl) {
        imagePreviewState.imageLoaded = false;
        imagePreviewState.isImageLoading = true;
    }

    imagePreviewState.previewUrl = imageGalleryItem.previewUrl;
    imagePreviewState.downloadUrl = imageGalleryItem.downloadUrl;
    imagePreviewState.id = imageGalleryItem.id;
});

mutator(setNativePdfPreviewState, actionMessage => {
    const { viewState, previewUrl, printUrl, mediaType, optionalParameters } = actionMessage;

    const nativePdfPreviewState: NativePdfPreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.NativePdf,
        previewUrl: previewUrl,
        printUrl: printUrl,
        mediaType: mediaType,
    };

    viewState.previewPane = nativePdfPreviewState;
});

mutator(setPdfJsPreviewState, actionMessage => {
    const { viewState, previewUrl, printDisabled, optionalParameters } = actionMessage;
    const pdfJsPreviewState: PdfJsPreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.PdfJs,
        previewUrl,
        data: null,
        printDisabled,
        copyAllowed: true,
        exportAllowed: true,
        passwordStatus: PdfJsPasswordState.Default,
    };

    viewState.previewPane = pdfJsPreviewState;
});

mutator(setPdfJsPreviewStateByData, actionMessage => {
    const {
        viewState,
        data,
        printDisabled,
        copyAllowed,
        exportAllowed,
        optionalParameters,
    } = actionMessage;
    const pdfJsPreviewState: PdfJsPreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.PdfJs,
        previewUrl: null,
        data,
        printDisabled,
        copyAllowed,
        exportAllowed,
        passwordStatus: PdfJsPasswordState.Default,
    };

    viewState.previewPane = pdfJsPreviewState;
});

mutator(setPreviewError, actionMessage => {
    const { viewState, errorMessage, optionalParameters } = actionMessage;
    const originalOptionalParameters: PreviewPaneViewStateBase = viewState.previewPane;
    const errorViewState: PreviewPaneErrorViewState = {
        ...(optionalParameters || originalOptionalParameters),
        mode: PreviewPaneMode.Error,
        errorMessage: errorMessage,
    };

    viewState.previewPane = errorViewState;
});

mutator(setPreviewLoading, actionMessage => {
    const { viewState, message, optionalParameters } = actionMessage;
    const originalOptionalParameters: PreviewPaneViewStateBase = viewState.previewPane;
    const loadingViewState: PreviewPaneLoadingViewState = {
        ...(optionalParameters || originalOptionalParameters),
        mode: PreviewPaneMode.Loading,
        message: message,
    };

    viewState.previewPane = loadingViewState;
});

mutator(setSaveStatusText, actionMessage => {
    const {
        viewState: { previewPane },
        text,
    } = actionMessage;

    if (previewPane.mode === PreviewPaneMode.Wac) {
        const wacPreviewState = previewPane as WacPreviewState;
        wacPreviewState.saveStatusText = text;
    }
});

mutator(setTextPreviewState, actionMessage => {
    const { viewState, text, allowDownload, optionalParameters } = actionMessage;
    const textPreviewState: TextPreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.Text,
        allowDownload: allowDownload,
        text: text,
    };

    viewState.previewPane = textPreviewState;
});

mutator(setVideoPreviewState, actionMessage => {
    const { viewState, previewUrl, optionalParameters } = actionMessage;
    const videoPreviewState: VideoPreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.Video,
        previewUrl: previewUrl,
    };

    viewState.previewPane = videoPreviewState;
});

mutator(setWacPreviewState, actionMessage => {
    const {
        viewState,
        wacUrl,
        jsApiInfo,
        accessToken,
        accessTokenTtl,
        additionalParameters,
        deprecatedTokenAndParameters,
        clickTime,
        readOnly,
        optionalParameters,
    } = actionMessage;
    const wacPreviewState: WacPreviewState = {
        ...optionalParameters,
        mode: PreviewPaneMode.Wac,
        wacUrl: wacUrl,
        saveStatusText: null,
        jsApiInfo: jsApiInfo,
        accessToken: accessToken,
        accessTokenTtl: accessTokenTtl,
        additionalParameters: additionalParameters,
        deprecatedTokenAndParameters: deprecatedTokenAndParameters,
        readOnly: readOnly,
        clickTime: clickTime || new Date().getTime(),
    };

    viewState.previewPane = wacPreviewState;
});

mutator(setWacReadOnly, actionMessage => {
    const { viewState, readOnly } = actionMessage;

    if (viewState.previewPane.mode === PreviewPaneMode.Wac) {
        const previewPaneState = viewState.previewPane as WacPreviewState;
        previewPaneState.readOnly = readOnly;
    }
});

mutator(setPdfJsPasswordState, actionMessage => {
    const { viewState, passwordState } = actionMessage;
    viewState.passwordStatus = passwordState;
});
